'use client';

// Client-side thumbnail renderer for Gear catalogue products — the Gear rail
// counterpart to furniture/furnitureThumbnail.ts. One shared offscreen WebGL
// renderer snaps a 3/4 "hero" view of a product's recipe to a PNG data URL,
// cached by product id (never the recipe itself: `buildProductObject`
// already caches by recipe internally, and product ids are what the rail and
// product view key their thumbnails by).
//
// `buildProductObject` is the ONLY place a catalogue recipe becomes a mesh
// (AGENTS.md) — this file must never build geometry itself, only frame and
// render what that function returns.

import * as THREE from 'three';
import { buildProductObject } from './productMesh';
import type { Product } from '@roomtreatment/av-catalog';

/** Rendered once per product at this resolution and reused (via CSS) for
 *  both the 48px rail row and the 160px product view — sharp at either. */
const SIZE = 320;

const cache = new Map<string, string>();
let renderer: THREE.WebGLRenderer | null = null;

function getRenderer(): THREE.WebGLRenderer {
  if (!renderer) {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(SIZE, SIZE);
    renderer.setPixelRatio(1);
    renderer.setClearColor(0x000000, 0);
  }
  return renderer;
}

/** Render `product`'s recipe to a PNG data URL, framed 3/4 on a transparent
 *  background. Synchronous under the hood (no asset to await — the recipe is
 *  already in memory), but returns a Promise so callers use the same
 *  load-then-set-state shape as `furnitureThumbnail`. Cached by product id. */
export function productThumbnail(product: Product): Promise<string> {
  const cached = cache.get(product.id);
  if (cached) return Promise.resolve(cached);

  const r = getRenderer();
  const scene = new THREE.Scene();
  scene.add(new THREE.AmbientLight(0xffffff, 1.15));
  const key = new THREE.DirectionalLight(0xffffff, 1.5);
  key.position.set(2.5, 4, 3);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xffffff, 0.5);
  fill.position.set(-3, 1.5, -2);
  scene.add(fill);

  // `buildProductObject` returns a clone from its own cache and owns the
  // shared geometries/materials (see productMesh.ts) — never dispose them.
  const model = buildProductObject(product.recipe);
  const box = new THREE.Box3().setFromObject(model);
  const size = new THREE.Vector3();
  box.getSize(size);
  const center = new THREE.Vector3();
  box.getCenter(center);
  model.position.sub(center);
  scene.add(model);

  const maxDim = Math.max(size.x, size.y, size.z) || 1;
  const cam = new THREE.PerspectiveCamera(35, 1, maxDim * 0.05, maxDim * 12);
  cam.position.copy(new THREE.Vector3(1, 0.8, 1).normalize().multiplyScalar(maxDim * 2.4));
  cam.lookAt(0, 0, 0);

  r.render(scene, cam);
  const dataUrl = r.domElement.toDataURL('image/png');

  scene.remove(model); // the clone's own geometry/material references stay shared — nothing to dispose

  cache.set(product.id, dataUrl);
  return Promise.resolve(dataUrl);
}
